import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  ImageCropperComponent,
  ImageCropperModule,
  ImageTransform
} from 'ngx-image-cropper';
import { ImageCroppedEvent } from '@App/common/interfaces';
import {
  ToggleSwitchChangeEvent,
  ToggleSwitchModule
} from 'primeng/toggleswitch';
import { TranslateModule } from '@ngx-translate/core';
import { SliderModule } from 'primeng/slider';
import { FormsModule } from '@angular/forms';
import { NgIf, NgStyle } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonDirective } from 'primeng/button';

@Component({
  selector: 'app-image-cropper-dialog',
  styleUrls: ['./image-cropper-dialog.component.scss'],
  templateUrl: './image-cropper-dialog.component.html',
  standalone: true,
  imports: [
    DialogModule,
    ToggleSwitchModule,
    FormsModule,
    SliderModule,
    TranslateModule,
    NgStyle,
    NgIf,
    ImageCropperModule,
    ButtonDirective
  ]
})
export class ImageCropperDialogComponent {
  @Input() visible = false;
  @Input() dialogWidth = '400px';
  @Input() isSwitcherCropperActive = false;
  @Input() isRoundCropper = false;
  @Input() aspectRatio = 16 / 9;
  @Input() containWithinAspectRatio = false;
  @Input() cropperMinHeight = 250;
  @Input() cropperMinWidth = 250;
  @Input() imageQuality = 92;
  @Input() imageURL = '';
  @Input() maintainAspectRatio = true;
  @Input() resizeToHeight = 250;
  @Input() resizeToWidth = 250;
  @Input() transform: ImageTransform = { scale: 1 };
  @Input() zoomValue = 1;
  @Input() minZoom = 0.01;
  @Input() maxZoom = 2;
  @Input() showCropper = false;

  @Output() visibleChange = new EventEmitter<boolean>(); // supports [(visible)] two-way binding
  @Output() imageCroppedEvent = new EventEmitter<ImageCroppedEvent>();
  @Output() imageLoadedEvent = new EventEmitter<void>();
  @Output() loadImageFailedEvent = new EventEmitter<void>();
  @Output() zoomChangedEvent = new EventEmitter<ImageTransform>();
  @Output() zoomValueChangedEvent = new EventEmitter<number>();
  @Output() roundCropperToggled = new EventEmitter<boolean>();
  @Output() cropClicked = new EventEmitter<void>();

  onVisibleChange(value: boolean) {
    this.visible = value;
    this.visibleChange.emit(value);
  }

  onImageCropped(event: ImageCroppedEvent) {
    this.imageCroppedEvent.emit(event);
  }

  onImageLoaded() {
    this.imageLoadedEvent.emit();
  }

  onLoadImageFailed() {
    this.loadImageFailedEvent.emit();
  }

  onWheel(event: WheelEvent) {
    let scale = this.transform.scale ?? 1;
    scale =
      event.deltaY > 0
        ? parseFloat((scale + 0.08).toFixed(2))
        : parseFloat((scale - 0.08).toFixed(2));
    scale = Math.min(this.maxZoom, Math.max(this.minZoom, scale));

    this.transform = { scale };
    this.zoomValue = scale;
    this.zoomChangedEvent.emit(this.transform);
    this.zoomValueChangedEvent.emit(this.zoomValue);
  }

  onZoomSlider(event: any) {
    this.transform = { scale: event.value };
    this.zoomValue = event.value;
    this.zoomChangedEvent.emit(this.transform);
    this.zoomValueChangedEvent.emit(this.zoomValue);
  }

  onToggleRoundCropper({ checked }: ToggleSwitchChangeEvent) {
    this.isRoundCropper = checked;
    this.roundCropperToggled.emit(checked);
  }

  onCrop() {
    this.cropClicked.emit();
  }
}
